import type { ReactNode } from 'react'

/**
 * Sidebar nav line-icons — one per primary/account/manage item, keyed by the
 * item's route intent. Paths reuse the owner-approved sidebar mockup so the
 * expanded rail matches what was signed off (Admin is added in the same stroke
 * style — the mockup has no admin row). Stroke uses currentColor so each nav
 * row drives colour + glow; the icons are decorative (aria-hidden) — the row's
 * visible label (or its collapsed aria-label / title) carries the name.
 */
function Icon({ children }: { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  )
}

export type NavIconKey =
  | 'home'
  | 'play'
  | 'clubs'
  | 'players'
  | 'tournaments'
  | 'ladders'
  | 'guides'
  | 'product'
  | 'pricing'
  | 'workspace'
  | 'admin'
  | 'profile'
  | 'help'
  | 'logout'

export const navIcons: Record<NavIconKey, ReactNode> = {
  home: (
    <Icon>
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5 9.5V21h14V9.5" />
    </Icon>
  ),
  play: (
    <Icon>
      <circle cx="12" cy="12" r="9" />
      <path d="M4.5 6.5c4 3 7 6 13 13M19.5 6.5c-4 3-7 6-13 13" />
    </Icon>
  ),
  clubs: (
    <Icon>
      <path d="M4 21V6l7-3v18M11 21V9l7 3v9" />
      <path d="M2 21h20" />
    </Icon>
  ),
  players: (
    <Icon>
      <circle cx="8.5" cy="8" r="3" />
      <path d="M3 20c0-3 2.5-5 5.5-5s5.5 2 5.5 5" />
      <circle cx="17" cy="9" r="2.4" />
      <path d="M15 20c0-2.5 1.6-4 3.8-4" />
    </Icon>
  ),
  tournaments: (
    <Icon>
      <path d="M6 4h12v3a6 6 0 0 1-12 0Z" />
      <path d="M6 5H3.5v1.5A3 3 0 0 0 6.5 9.4M18 5h2.5v1.5A3 3 0 0 1 17.5 9.4" />
      <path d="M12 13v4M9 20h6M10 17h4" />
    </Icon>
  ),
  ladders: (
    <Icon>
      <path d="M8 3v18M16 3v18M8 7h8M8 12h8M8 17h8" />
    </Icon>
  ),
  guides: (
    <Icon>
      <path d="M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2Z" />
      <path d="M4 19a2 2 0 0 0 2 2h13" />
    </Icon>
  ),
  product: (
    <Icon>
      <path d="M12 3 3 8l9 5 9-5-9-5Z" />
      <path d="M3 12l9 5 9-5" />
      <path d="M3 16l9 5 9-5" />
    </Icon>
  ),
  pricing: (
    <Icon>
      <path d="M4 4h7l9 9-7 7-9-9V4Z" />
      <circle cx="8" cy="8" r="1.3" />
    </Icon>
  ),
  workspace: (
    <Icon>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </Icon>
  ),
  admin: (
    <Icon>
      <path d="M12 3l7 3v5c0 4.2-2.8 7.4-7 8.5C7.8 18.4 5 15.2 5 11V6Z" />
      <path d="m9.5 12 1.8 1.8L15 10" />
    </Icon>
  ),
  profile: (
    <Icon>
      <circle cx="12" cy="8.5" r="3.5" />
      <path d="M5 20c0-3.5 3-6 7-6s7 2.5 7 6" />
    </Icon>
  ),
  help: (
    <Icon>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.6 9.4a2.5 2.5 0 0 1 4.8.9c0 1.7-2.4 2.2-2.4 3.7" />
      <path d="M12 17h.01" />
    </Icon>
  ),
  logout: (
    <Icon>
      <path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3" />
      <path d="M10 12h9M16 8l4 4-4 4" />
    </Icon>
  ),
}
